import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuthentication, useFlightRooms, useWorkstation } from '@connect-x-app/hybrid-state';
import { FlightRoomSummary, FlightStatus, NotificationType, QueryKeys } from '@connect-x-app/shared-domain-models';
import { getOpenTaskChannelId, getOpenTaskMessageId, hasOpenTasks, isRoomLanded } from '@connect-x-app/shared-utils';
import { FLIGHTROOM_TEST_IDS } from '@connect-x-app/web-test-ids';
import { ConfirmationModal, ModalOpener } from '@connect-x-app/web-ui';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';

import { FlightRoomList } from './components/FlightRoomList';
import { FlightRoomsHeaderComponent } from './components/FlightRoomsHeaderComponent/FlightRoomsHeaderComponent';
import { RequestFlightroomAccessModal } from './components/requestFlightroomAccessModal/RequestFlightroomAccessModal';
import { SearchedFlightRoomList } from './components/SearchedFlightRoomList';
import { Workstation } from './components/Workstation/Workstation';
import { useFlightroomJoinDeepLinkAction } from './hooks/useFlightroomJoinDeepLinkAction';

import './Flightrooms.scss';

export const Flightrooms = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canViewTasks } = useAuthentication();
  const {
    handleWorkstationChange,
    handleWorkstationClose,
    handleWorkstationDelete,
    handleWorkstationOpen,
    handleWorkstationReset,
    handleWorkstationSubmit,
    isWorkstationChanged,
    isWorkstationOpen,
    workstation,
    workstationDraft,
  } = useWorkstation({
    onUpdateSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.FLIGHTROOMS] });
    },
  });

  const {
    activeFlightRooms,
    flightRoomsWithTask,
    landedFlightRooms,
    selectedFlightRoom,
    selectedFlightRoomTab,
    setSelectedFlightRoomTab,
  } = useFlightRooms();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();
  const notificationType = searchParams.get('type');

  const { requestAccessExternalId, requestAccessRoomId } = useFlightroomJoinDeepLinkAction();

  useEffect(() => {
    if (!requestAccessRoomId) {
      return;
    }
    ModalOpener.instance.open({
      render: () => (
        <RequestFlightroomAccessModal
          chatroomId={requestAccessRoomId}
          content={t('FLIGHTROOMS.REQUEST_ACCESS.MESSAGE_BY_ID', { legId: requestAccessExternalId ?? requestAccessRoomId })}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: [QueryKeys.FLIGHTROOMS] })}
          title={t('FLIGHTROOMS.REQUEST_ACCESS.TITLE')}
        />
      ),
    });
  }, [queryClient, requestAccessExternalId, requestAccessRoomId, t]);

  useEffect(() => {
    if (selectedFlightRoom) {
      if (hasOpenTasks(selectedFlightRoom.tasks)) {
        setSelectedFlightRoomTab(FlightStatus.TASKS);
        return;
      }
      if (isRoomLanded(selectedFlightRoom)) {
        setSelectedFlightRoomTab(FlightStatus.LANDED);
        return;
      }
      setSelectedFlightRoomTab(FlightStatus.ACTIVE);
    }
  }, [selectedFlightRoom, setSelectedFlightRoomTab]);

  useEffect(() => {
    if (!canViewTasks && selectedFlightRoomTab === FlightStatus.TASKS) {
      setSelectedFlightRoomTab(FlightStatus.ACTIVE);
    }
  }, [canViewTasks, selectedFlightRoomTab, setSelectedFlightRoomTab]);

  useEffect(() => {
    if (notificationType === NotificationType.EXTERNAL_MENTION && canViewTasks && !selectedFlightRoom) {
      setSelectedFlightRoomTab(FlightStatus.TASKS);
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.delete('type');
          return next;
        },
        { replace: true },
      );
    }
  }, [notificationType, canViewTasks, selectedFlightRoom, setSelectedFlightRoomTab, setSearchParams]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKeys.WORKSTATION] });
  }, [isWorkstationOpen, queryClient, selectedFlightRoomTab]);

  const handleTabChange = useCallback(
    (newTab: FlightStatus) => {
      setSelectedFlightRoomTab(newTab);
    },
    [setSelectedFlightRoomTab],
  );

  const flightRooms = useMemo(() => {
    switch (selectedFlightRoomTab) {
      case FlightStatus.LANDED:
        return landedFlightRooms;
      case FlightStatus.TASKS:
        return flightRoomsWithTask;
      case FlightStatus.ACTIVE:
      default:
        return activeFlightRooms;
    }
  }, [activeFlightRooms, landedFlightRooms, flightRoomsWithTask, selectedFlightRoomTab]);

  const handleFlightRoomClick = (room: FlightRoomSummary) => {
    const taskChannelId = getOpenTaskChannelId(room.tasks);
    const taskMessageId = getOpenTaskMessageId(room.tasks);
    const queryParams = taskMessageId ? `?externalMessageId=${taskMessageId}` : '';
    if (taskChannelId) {
      navigate(`/dashboard/flightrooms/${room.id}/channel/${taskChannelId}${queryParams}`);
      return;
    }
    navigate(`/dashboard/flightrooms/${room.id}${queryParams}`);
  };

  const onHandleWorkstationClose = () => {
    if (isWorkstationChanged) {
      ModalOpener.instance.open({
        render: () => (
          <ConfirmationModal
            cancelButtonTheme="dark"
            confirmButtonTheme="negative"
            confirmText={t('WORKSTATION.MODAL.CONFIRM')}
            content={t('WORKSTATION.MODAL.WORKSTATION_CHANGED')}
            onConfirm={handleWorkstationClose}
            title={t('WORKSTATION.MODAL.ABANDON_CHANGES')}
          />
        ),
      });
    } else {
      handleWorkstationClose();
    }
  };

  const onHandleWorkstationDelete = () => {
    ModalOpener.instance.open({
      render: () => (
        <ConfirmationModal
          cancelButtonTheme="dark"
          confirmText={t('WORKSTATION.MODAL.CONFIRM')}
          content={t('WORKSTATION.MODAL.WORKSTATION_DELETE')}
          onConfirm={handleWorkstationDelete}
          title={t('WORKSTATION.MODAL.DELETE_WORKSTATION')}
        />
      ),
    });
  };

  const onHandleWorkstationReset = () => {
    ModalOpener.instance.open({
      render: () => (
        <ConfirmationModal
          cancelButtonTheme="dark"
          confirmText={t('WORKSTATION.MODAL.CONFIRM')}
          content={t('WORKSTATION.MODAL.WORKSTATION_CLEAR')}
          onConfirm={handleWorkstationReset}
          title={t('WORKSTATION.MODAL.CLEAR_WORKSTATION')}
        />
      ),
    });
  };

  return (
    <>
      <aside className="flightrooms-container">
        <FlightRoomsHeaderComponent
          handleWorkstationClose={onHandleWorkstationClose}
          searchQuery={searchQuery}
          selectedFlightroomTab={selectedFlightRoomTab}
          setSearchQuery={setSearchQuery}
          setSelectedFlightroomTab={handleTabChange}
          workstation={workstation}
        />
        {searchQuery ? (
          <SearchedFlightRoomList resetSearchQuery={() => setSearchQuery('')} searchQuery={searchQuery} />
        ) : (
          <FlightRoomList flightRooms={flightRooms} onFlightRoomClick={handleFlightRoomClick} workstation={workstation} />
        )}
      </aside>
      <div
        className={classNames({ 'flightrooms-info': true, 'flightrooms-info--hidden': !isWorkstationOpen })}
        data-test={FLIGHTROOM_TEST_IDS.workstation.panel}
      >
        <Workstation
          handleWorkstationChange={handleWorkstationChange}
          handleWorkstationClose={onHandleWorkstationClose}
          handleWorkstationDelete={onHandleWorkstationDelete}
          handleWorkstationOpen={handleWorkstationOpen}
          handleWorkstationReset={onHandleWorkstationReset}
          handleWorkstationSubmit={handleWorkstationSubmit}
          isWorkstationChanged={isWorkstationChanged}
          isWorkstationOpen={isWorkstationOpen}
          workstationDraft={workstationDraft}
        />
      </div>
    </>
  );
};
